<template lang="pug">
.profiles(ref="scrollContainer", data-qa="step0-profiles-list-container")
  Loader(v-if="deletingProfile", :filling="true")

  Search(v-model="filter" :label="$t('search')")
  .profile-list.flex.flex-col
    div(v-for="profile in profilesToRender" :class="`profile-list__${profile.PROFILE_ID}`" :key="profile.PROFILE_ID")
      .profile.border-2.flex.flex-col.text-sm(
        :class=`{
          'border-accentColor': selected === profile.PROFILE_ID,
          'border-gray-2': selected !== profile.PROFILE_ID,
        }`,
        :data-profile-id="profile.PROFILE_ID",
        :data-qa="'step0-profile-' + profile.PROFILE_ID",
        @click="selectProfile(profile.PROFILE_ID)"
      )
        .profile__header
          h1.title-lg {{ getProfileName(profile) }}
          div(class="flex items-center flex-[0_0_56px]")
            EditIcon(class="flex-[0_0_36px] cursor-pointer px-2", @click.stop="editProfile(profile.PROFILE_ID)", data-qa="step0-edit-profile-icon")
            DeleteProfile(class="flex-[0_0_36px] cursor-pointer pl-2", @click.stop="deleteProfile(profile)", data-qa="step0-delete-profile-icon")
        .profile__chips
          .profile__chips-default(v-if="profile.PROFILE_ID === defaultProfile") {{ $t('default_profile') }}
        .profile__main
          .description-xl-dark {{ profile.ADDRESS_INFO_FIELDS.FIO }}
          .description-xl-muted {{ getAddress(profile) }}
          
          //- Блок дополнительного адреса (фактуры)
          .profile__additional(v-if="getAdditionalAddress(profile)")
            .description-xl-dark {{ $t('invoice_address') }}
            .description-xl-muted {{ getAdditionalAddress(profile) }}
          
          .profile__main-days
            p.description-xl-dark {{ getDeliveryService(profile) }}
            Skeletor(v-if="profilePricesLoading", :width="60", :height="20")
            span.description-xl-success(v-else) {{ getDeliveryPrice(profile) }}
</template>

<script>
import Search from './Search.vue'
import Loader from '@/components/Loader.vue'
import { createAddress, createAdditionalAddress } from '@/global/createAddress'
import { mapState } from 'vuex'
import { declOfNumGlobal, formatMoneyGlobal } from '@/utils/delivery'
import DeleteProfile from '@/components/svg/DeleteProfile.vue'
import EditIcon from '@/components/svg/EditIcon.vue'

export default {
  name: 'ListNew',
  components: { DeleteProfile, Search, Loader, EditIcon },
  props: {
    selected: { type: Number, default: 0 },
  },
  data() {
    return {
      filter: '',
      deletingProfile: false,
      profilesToShow: 10,
      scrollContainer: null,
    }
  },
  computed: {
    ...mapState([
      'deliveryTypes',
      'deliveryTypesWithCityObject',
      'deliveryProfiles',
      'profilePrices',
      'profilePricesLoading',
    ]),
    defaultProfile() {
      const selected = this.deliveryProfiles.find((p) => p.LAST_SELECTED)
      return selected
        ? selected.PROFILE_ID
        : this.deliveryProfiles[0]?.PROFILE_ID
    },
    filteredProfiles() {
      let list = this.deliveryProfiles
      if (this.filter) {
        const q = this.filter.toLowerCase()
        list = list.filter((p) => {
          const text = [
            this.getProfileName(p),
            p.ADDRESS_INFO_FIELDS.FIO,
            this.getAddress(p),
            this.getAdditionalAddress(p),
            this.getDeliveryService(p),
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          return text.includes(q)
        })
      }

      return list
    },
    filteredProfilesCount() {
      return this.filteredProfiles.length
    },
    profilesToRender() {
      return this.filteredProfiles.slice(0, this.profilesToShow)
    },
  },
  async mounted() {
    if (this.selected !== 0) {
      this.selectProfile(this.selected)
    } else {
      this.selectProfile(this.defaultProfile)
    }

    this.scrollContainer = this.$refs.scrollContainer

    if (this.scrollContainer) {
      this.scrollContainer.addEventListener('scroll', this.onScrollContainer)
    }
  },
  beforeUnmount() {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.onScrollContainer)
    }
  },
  methods: {
    onScrollContainer() {
      const el = this.scrollContainer
      if (!el) return

      const scrollTop = el.scrollTop
      const clientHeight = el.clientHeight
      const scrollHeight = el.scrollHeight

      const nearBottom = scrollTop + clientHeight >= scrollHeight - 100

      if (nearBottom && this.profilesToShow < this.filteredProfilesCount) {
        this.profilesToShow += 10
      }
    },
    checkFaktura(profile) {
      if (!profile.ADDITIONAL_INFO_FIELDS) return false
      for (const key in profile.ADDITIONAL_INFO_FIELDS) {
        if (
          !profile.ADDITIONAL_INFO_FIELDS[key] ||
          profile.ADDITIONAL_INFO_FIELDS[key] === ''
        )
          return false
      }
      return true
    },
    async getDeliveryWhenEdit(profile) {
      try {
        await this.$store.dispatch('GET_DELIVERY_TYPES', {
          CITY_NAME: profile.ADDRESS_INFO_FIELDS.CITY,
          AREA_NAME: profile.ADDRESS_INFO_FIELDS.AREA,
        })
      } catch (err) {
        console.log(err)
      }
    },
    deleteProfile(profile) {
      if (!profile) return
      this.$emit('showDeleteAlert', profile)
    },
    editProfile(profileID) {
      if (!profileID) return
      this.$emit('editProfile', profileID)
      this.$store.commit('setFioFieldFocusState', true)
    },
    hasDeliveryInfoInCity(profile) {
      const deliveryCity = profile.ADDRESS_INFO_FIELDS.CITY
      return this.deliveryTypesWithCityObject[deliveryCity]
    },
    selectProfile(profileID) {
      this.$emit('selectActiveProfile', profileID)
    },

    async removeProfile(profile) {
      this.deletingProfile = true
      const wasLastProfile = this.deliveryProfiles.length === 1
      let deletedSelected = false
      const deletedProfileId = profile.PROFILE_ID

      // Если удаляем выбранный профиль и есть еще профили
      if (this.selected === deletedProfileId && !wasLastProfile) {
        deletedSelected = true
        if (this.deliveryProfiles[0].PROFILE_ID !== this.selected) {
          this.selectProfile(this.deliveryProfiles[0].PROFILE_ID)
        } else {
          this.selectProfile(this.deliveryProfiles[1].PROFILE_ID)
        }
      }
      await this.$store.dispatch('DELETE_DELIVERY_PROFILE', profile.PROFILE_ID)
      this.$emit('afterDelete', {
        wasLastProfile,
        deletedSelected,
      })
      this.deletingProfile = false
    },

    getDeliveryService(profile) {
      if (profile.LAST_CHOICES?.LAST_DELIVERY_TYPE) {
        return profile.LAST_CHOICES.LAST_DELIVERY_TYPE?.SERVICE?.NAME
      } else {
        return this.deliveryTypes.find(
          (el) => el.ID === profile.ADDRESS_INFO_FIELDS.DELIVERY_TYPE
        )?.ITEMS?.[0]?.NAME
      }
    },
    getAddress(profile) {
      return createAddress(profile, this.$store.state.country, this.$t)
    },
    getAdditionalAddress(profile) {
      return createAdditionalAddress(profile)
    },
    getDeliveryPrice(p) {
      const priceData = this.profilePrices[p.PROFILE_ID]
      if (!priceData) return ''
      return priceData.deliveryPrice > 0
        ? this.formatMoney(priceData.deliveryPrice, priceData.currency)
        : this.$t('basket_free')
    },
    formatMoney(v, currency) {
      return formatMoneyGlobal(
        v,
        this.$store.state.country.code,
        currency || this.$store.state.currency
      )
    },
    declOfNum(number, titles) {
      return declOfNumGlobal(number, titles)
    },
    getProfileName(profile) {
      if (profile.LAST_CHOICES?.LAST_DELIVERY_TYPE) {
        return profile.LAST_CHOICES.LAST_DELIVERY_TYPE.NAME
      } else if (
        this.deliveryTypes.find(
          (el) => el.ID === profile.ADDRESS_INFO_FIELDS.DELIVERY_TYPE
        )
      ) {
        return this.deliveryTypes.find(
          (el) => el.ID === profile.ADDRESS_INFO_FIELDS.DELIVERY_TYPE
        ).NAME
      } else {
        return profile.ADDRESS_INFO_FIELDS.DELIVERY_TYPE
      }
    },
  },
}
</script>

<style lang="scss">
.profiles {
  max-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;

  & * {
    line-height: 1.25;
  }

  .profile {
    background-color: white;
    border-radius: 12px !important;
    padding: 16px;
    gap: 2px;
    margin-bottom: 8px;
    width: 100%;
    box-sizing: border-box;

    &__header {
      display: flex;
      align-items: start;
      justify-content: space-between;
      flex-wrap: wrap;
    }

    &__chips {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 14px;

      &-default {
        white-space: nowrap;
        padding: 2px 8px;
        border-radius: 4px;
        border: 1px solid rgba(237, 127, 47, 0.1);
        background: #ffe9c0;
        width: min-content;
        overflow: hidden;
        color: #e03b31;
        text-overflow: ellipsis;
        font-family: Rubik;
        font-size: 12px;
        font-weight: 400;
      }
    }

    &__main {
      display: flex;
      flex-direction: column;
      gap: 4px;

      &-days {
        display: flex;
        align-items: start;
        justify-content: space-between;
        gap: 12px;
        margin-top: 12px;
      }
    }

    &__additional {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid #f0f0f0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
  }
}
.old-profiles-block {
  background-color: #2f80ed14;
  padding: 16px;

  &__text {
    margin-right: 20px;
  }

  .btn-arrow {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    flex: 0 0 36px;
    display: flex;
    align-items: center;
    justify-content: center;

    .icon-arrow-right {
      width: 24px;
      height: 24px;
      flex: 0 0 24px;
    }
  }
}
.direction--rtl {
  .icon {
    width: 16px;
    height: 14px;
    flex: 0 0 16px;

    &.icon-edit {
      margin-right: auto;
      margin-left: 0;
    }
  }
}
</style>
